/**
 * CESM OSINT System - Coherence History Panel
 * Panneau d'administration pour le suivi des auto-réparations de données
 * 
 * Concept & Développement: CDT (R) Jean-Baptiste GIRAUD
 * LPP Logiciel © 2026
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wrench, AlertTriangle, CheckCircle, RefreshCw, 
  Database, TrendingUp, Clock, Filter, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CoherenceHistoryPanel = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedSource, setSelectedSource] = useState('');

  useEffect(() => {
    fetchStats();
    fetchHistory();
  }, [token]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/coherence/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching coherence stats:', error);
    }
  };

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      let url = `${API_URL}/api/admin/coherence/history?limit=50`;
      if (selectedSource) {
        url += `&source=${selectedSource}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching coherence history:', error);
    }
    setIsLoading(false);
  };

  const refreshData = () => {
    fetchStats();
    fetchHistory();
  };

  return (
    <div className="bg-slate-800/50 border border-amber-500/30 rounded-lg overflow-hidden" data-testid="coherence-history-panel">
      {/* Header */}
      <div className="bg-amber-900/30 px-4 py-3 border-b border-amber-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wrench className="w-5 h-5 text-amber-400" />
            <h3 className="font-mono text-amber-300 font-bold uppercase tracking-wider">
              Suivi Auto-Réparations
            </h3>
          </div>
          <button
            onClick={refreshData}
            className="p-2 hover:bg-amber-500/20 rounded transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`w-4 h-4 text-amber-400 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-black/30 p-3 rounded border border-amber-500/20">
            <div className="flex items-center gap-2 mb-1">
              <Database className="w-4 h-4 text-amber-400" />
              <span className="font-mono text-xs text-white/50 uppercase">Total Événements</span>
            </div>
            <p className="font-mono text-2xl text-amber-300 font-bold">
              {stats.total_repair_events || 0}
            </p>
          </div>
          
          <div className="bg-black/30 p-3 rounded border border-cyan-500/20">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-cyan-400" />
              <span className="font-mono text-xs text-white/50 uppercase">Aujourd'hui</span>
            </div>
            <p className="font-mono text-2xl text-cyan-300 font-bold">
              {stats.repairs_today || 0}
            </p>
          </div>
          
          <div className="bg-black/30 p-3 rounded border border-red-500/20 col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="font-mono text-xs text-white/50 uppercase">Sources Problématiques</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.top_problematic_sources?.slice(0, 3).map((source, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 bg-red-500/20 border border-red-500/30 rounded text-xs font-mono text-red-300"
                >
                  {source.source} ({source.incidents})
                </span>
              )) || <span className="text-white/30 text-xs font-mono">Aucune</span>}
            </div>
          </div>
        </div>
      )}

      {/* Top Problematic Fields */}
      {stats?.top_problematic_fields?.length > 0 && (
        <div className="px-4 pb-4">
          <div className="bg-black/30 p-3 rounded border border-purple-500/20">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <span className="font-mono text-xs text-white/50 uppercase">Champs les plus corrigés</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.top_problematic_fields.map((field, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded text-xs font-mono text-purple-300"
                >
                  {field.field} ({field.occurrences}x)
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* History Toggle */}
      <div className="border-t border-amber-500/20">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-amber-500/10 transition-colors"
        >
          <span className="font-mono text-sm text-white/70 uppercase tracking-wider">
            Historique détaillé ({history.length} événements)
          </span>
          {showHistory ? (
            <ChevronUp className="w-4 h-4 text-amber-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-amber-400" />
          )}
        </button>
      </div>

      {/* History List */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 max-h-96 overflow-y-auto space-y-3">
              {history.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500/30 mx-auto mb-3" />
                  <p className="font-mono text-white/50 text-sm">
                    Aucune réparation enregistrée
                  </p>
                  <p className="font-mono text-white/30 text-xs mt-1">
                    Le système fonctionne normalement
                  </p>
                </div>
              ) : (
                history.map((event, idx) => (
                  <div 
                    key={idx}
                    className="bg-black/30 p-3 rounded border border-slate-600/30"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs font-mono rounded">
                          {event.source}
                        </span>
                        <span className="text-white/50 text-xs font-mono">
                          {event.repair_count} correction(s)
                        </span>
                      </div>
                      <span className="text-white/30 text-xs font-mono">
                        {new Date(event.timestamp).toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {event.fields_affected?.map((field, fIdx) => (
                        <span 
                          key={fIdx}
                          className="px-1.5 py-0.5 bg-slate-700/50 text-white/60 text-xs font-mono rounded"
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CoherenceHistoryPanel;
